/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import 'ag-grid-enterprise';

import * as $ from 'jquery';

import ClickEvent    = JQuery.ClickEvent;
import KeyDownEvent = JQuery.KeyDownEvent;
import {ICellEditorComp, ICellEditorParams} from "ag-grid-community";

/**
 *
 * @class WholePositiveNumberEditor
 *
 * @brief helper for agGrid to allow integer only editing of cells
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2020
 *
 */
export class WholePositiveNumberEditor implements ICellEditorComp {

  /** agGrid DOM element for display */
  public gui: HTMLElement;

  /** the current value */
  public value: number;

  /** the callback to tell agGrid we're done editing */
  protected editingDone: (suppressNavigateAfterEdit?: boolean) => void = null;

  protected clickListenter: (e: MouseEvent) => void = null;

  /** helper to fetch a legit value from a number input */
  public parseNumber(el: JQuery) {

    if ($(el).val() === '') {

      $(el).val(0);

      return 0;
    }

    this.value  = Math.abs(parseInt($(el).val() as string));

    if (isNaN(this.value)) {

      this.value = 0;

      return 0;
    }

    /* reset the current value in the field */

    $(el).val(this.value);

    return this.value;
  }

  /** set up the custom editor (happens on every edit attempt) */
  public init(params: ICellEditorParams): void {

    this.editingDone = params.stopEditing;
    this.value       = null;

    const ce         = this;

    /* use existing cell value if there is one */

    if (params.value) {

      this.value = params.value;

    }

    /* did they press a key to start editing? */

    if ((params.eventKey ?? '').length === 1) {

      this.value = 0;

      if (/^\d$/.test(params.eventKey.charAt(0))) {

        this.value = parseInt(params.eventKey.charAt(0));
      }
    }

    if (isNaN(this.value)) {

      this.value = 0;
    }

    /* opened with Delete? */

    if ((params.eventKey ?? '').length === 1 && params.eventKey.charCodeAt(0) === 46) {

      this.value = 0;
    }

    /* set up the mini-form... */

    this.gui          = document.createElement('div');

    let form   = `<input tabindex="1" class="ag-grid-number-input" type="text" value="${this.value}">`;

    const html = `
<div class="accept-button" style="margin-left: 2px;">
  <i class="fas fa-check"></i>
</div>
<div class="ag-grid-number-form">
${form}
</div>`;

    $(this.gui).html(html);

    /* add "accept" behavior; clicking the accept button */

    const    el: JQuery = $(this.gui).find('.ag-grid-number-input');

    $(this.gui).find('.accept-button').on('click', (e: ClickEvent) => {

      e.preventDefault();

      this.value = this.parseNumber(el);

      /* tell agGrid we're done editing */

      this.editingDone(true);
    });

    setTimeout( () => {

      $(el).trigger('focus');

      $(el).val('');
      $(el).val(this.value);

      const len: number = ($(el).val() as string).length;
      ($(el).get(0) as HTMLInputElement).setSelectionRange(len, len);

    }, 100);

    $(el).on('change', () => {

      this.value = this.parseNumber(el);

    });

    /* must be a floating point number */

    $(el).on('keydown', (e: KeyDownEvent) => {

      if ((e.key === 'Escape') || (e.key === 'Enter') || (e.key === 'Tab')) {

        this.value = this.parseNumber(el);

        e.preventDefault();
        e.stopPropagation();

        this.editingDone(true);
      }

    });

    /*
     * watch for clicks outside the editor, implies closing the editor; the editor popups
     * are modal, there should only be one at a time.
     *
     */

    this.clickListenter = (e: MouseEvent) => {

      let target = e.target;

      do {

        if (target === this.gui) {

          /* it's ok, somewhere inside */

          return;
        }

        // @ts-ignore
        target = target.parentNode;

      } while (target);

      /* click is somewhere outside, auto-close */

      document.removeEventListener('click', ce.clickListenter);

      this.editingDone(true);
    };

    document.addEventListener('click', ce.clickListenter);

  }

  /** focus on local editing control (after it's attached to agGrid) */
  public afterGuiAttached(): void {

    const    el: JQuery = $(this.gui).find('.ag-grid-number-input');

    el.trigger('focus');
    el.trigger('click');
  }

  /* Return the DOM element of your editor, this is what the grid puts into the DOM */
  public getGui(): HTMLElement {

    return this.gui;
  }

  /* Should return the final value to the grid, the result of the editing */
  public getValue(): number {

    return this.value;
  }

  /** do any clean up */
  destroy(): void {

    /* clear out our loss of focus listener */

    document.removeEventListener('click', this.clickListenter);

    /* remove the DOM we inserted */

    $(this.gui).remove();
  }

  /** return true to be placed in a popup */
  public isPopup(): boolean {

    return true;
  }

  /** return the positioning, either 'over' or 'under' (only for popups) */
  getPopupPosition(): 'over' | 'under' | undefined   {

    return 'over';
  }

  /** return true to disallow editing before it even starts */
  isCancelBeforeStart(): boolean {

    return false;
  }

  /** return true to ignore editing change */
  isCancelAfterEnd(): boolean {

    return false;
  }

  /** on tabbing into the cell */
  public focusIn(): boolean {

    return true;
  }

  /** on tabbing out of the cell */
  public focusOut(): boolean {

    return true;
  }
}
